// Dev sign-in harness. Not the real register sign-in (that ships with
// P1-06). This page lets a developer verify the PinPad -> API -> JWT
// round-trip against a locally-running API.
//
// Fixture ids match the dev seed in apps/api/src/index.ts
// (createProductionRepo's in-memory fallback). If DATABASE_URL is set on
// the API, those fixtures won't exist and this page shows the API's
// error back to the developer.

import { PinPad } from "@betterpos/ui";
import { useState } from "react";
import { clearSessionToken, writeSessionToken } from "../data/audit.ts";
import { signIn } from "../data/auth.ts";

const DEV_DEVICE_ID = "01931600-0000-7000-8000-000000000011";
const DEV_DEVICE_CREDENTIAL = "device-secret-abc123";
const DEV_STAFF_ID = "01931600-0000-7000-8000-000000000001";
const DEV_STAFF_NAME = "Jake M.";
const DEV_STAFF_INITIALS = "JM";
const PIN_LENGTH = 6;

type State =
  | { kind: "idle" }
  | { kind: "signing_in" }
  | { kind: "signed_in"; token: string; sessionId: string }
  | { kind: "error"; status: number; error: string };

export function SignIn() {
  const [pin, setPin] = useState("");
  const [state, setState] = useState<State>({ kind: "idle" });
  const [showDevDetails, setShowDevDetails] = useState(false);

  const onComplete = async (value: string) => {
    setState({ kind: "signing_in" });
    const result = await signIn({
      deviceId: DEV_DEVICE_ID,
      deviceCredential: DEV_DEVICE_CREDENTIAL,
      staffId: DEV_STAFF_ID,
      pin: value,
    });
    if (result.ok) {
      writeSessionToken(result.token);
      setState({ kind: "signed_in", token: result.token, sessionId: result.sessionId });
    } else {
      setState({ kind: "error", status: result.status, error: result.error });
      setPin("");
    }
  };

  const reset = () => {
    clearSessionToken();
    setPin("");
    setShowDevDetails(false);
    setState({ kind: "idle" });
  };

  if (state.kind === "signed_in") {
    return (
      <main className="min-h-screen bg-ios-grouped-background flex items-center justify-center p-6 font-sans">
        <div className="w-full max-w-sm space-y-8">
          <header className="flex flex-col items-center space-y-4 pt-4">
            <AvatarBadge initials={DEV_STAFF_INITIALS} checked />
            <div className="text-center space-y-1">
              <h1 className="text-[22px] font-semibold text-slate-900 tracking-tight">Signed in</h1>
              <p className="text-[15px] text-slate-500">{DEV_STAFF_NAME}</p>
            </div>
          </header>

          <nav
            aria-label="After sign-in"
            className="rounded-2xl bg-white border border-slate-200/70 overflow-hidden"
          >
            <SettingsRow href="#/catalog" label="Products" />
            <SettingsRow href="#/audit" label="Audit log" />
          </nav>

          <nav
            aria-label="Session"
            className="rounded-2xl bg-white border border-slate-200/70 overflow-hidden"
          >
            <button
              type="button"
              onClick={reset}
              className="w-full h-[52px] px-4 text-left text-[17px] font-normal text-ios-red active:bg-slate-100 transition"
            >
              Sign out
            </button>
          </nav>

          <details
            className="px-1"
            open={showDevDetails}
            onToggle={(e) => setShowDevDetails((e.target as HTMLDetailsElement).open)}
          >
            <summary className="cursor-pointer text-[12px] uppercase tracking-[0.06em] font-semibold text-slate-400 select-none">
              Developer details
            </summary>
            <dl className="mt-3 rounded-2xl bg-white border border-slate-200/70 p-4 space-y-3 font-mono text-[11px]">
              <div>
                <dt className="text-slate-400">session_id</dt>
                <dd className="text-slate-700 break-all">{state.sessionId}</dd>
              </div>
              <div>
                <dt className="text-slate-400">token</dt>
                <dd className="text-slate-700 break-all">{state.token}</dd>
              </div>
            </dl>
          </details>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-ios-grouped-background flex items-center justify-center p-6 font-sans">
      <div className="w-full max-w-xs flex flex-col items-center space-y-8 pt-4">
        <header className="flex flex-col items-center space-y-4">
          <AvatarBadge initials={DEV_STAFF_INITIALS} />
          <div className="flex flex-col items-center space-y-1">
            <p className="text-[17px] font-medium text-slate-900">{DEV_STAFF_NAME}</p>
            <p className="text-[13px] text-slate-500">Enter passcode</p>
          </div>
        </header>

        <PinDisplay value={pin} max={PIN_LENGTH} error={state.kind === "error"} />

        <PinPad value={pin} onChange={setPin} maxLength={PIN_LENGTH} onComplete={onComplete} />

        <div className="min-h-6 text-center text-[14px]">
          {state.kind === "signing_in" && <span className="text-slate-400">Signing in…</span>}
          {state.kind === "error" && (
            <button
              type="button"
              onClick={() => setState({ kind: "idle" })}
              className="font-medium text-ios-red"
            >
              Try again
            </button>
          )}
        </div>
      </div>
    </main>
  );
}

function AvatarBadge({ initials, checked = false }: { initials: string; checked?: boolean }) {
  return (
    <div className="relative">
      <div
        role="img"
        aria-label={`Avatar ${initials}`}
        className="h-[72px] w-[72px] rounded-full bg-white border border-slate-200/70 shadow-[0_1px_2px_rgba(0,0,0,0.04)] grid place-items-center"
      >
        <span className="text-[26px] font-semibold text-slate-500 tracking-tight">{initials}</span>
      </div>
      {checked ? (
        <div className="absolute -right-1 -bottom-1 h-7 w-7 rounded-full bg-ios-blue grid place-items-center ring-2 ring-ios-grouped-background">
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            stroke="white"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M2.5 7.5L5.5 10.5L11.5 3.5" />
          </svg>
        </div>
      ) : null}
    </div>
  );
}

function SettingsRow({ href, label }: { href: string; label: string }) {
  return (
    <a
      href={href}
      className="flex items-center justify-between h-[52px] px-4 text-[17px] text-slate-900 active:bg-slate-100 transition border-b border-slate-200/60 last:border-b-0"
    >
      <span>{label}</span>
      <svg
        width="8"
        height="14"
        viewBox="0 0 8 14"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-slate-400"
        aria-hidden="true"
      >
        <path d="M1 1L7 7L1 13" />
      </svg>
    </a>
  );
}

function PinDisplay({ value, max, error }: { value: string; max: number; error: boolean }) {
  const dots = Array.from({ length: max }, (_, i) => ({
    key: `dot-${i}`,
    filled: i < value.length,
  }));
  return (
    <output
      className={`flex justify-center gap-5 ${error ? "animate-[shake_0.3s]" : ""}`}
      aria-label={`${value.length} of ${max} digits entered`}
    >
      {dots.map(({ key, filled }) => (
        <span
          key={key}
          className={`h-[14px] w-[14px] rounded-full transition-colors ${
            error
              ? "bg-ios-red"
              : filled
                ? "bg-slate-900"
                : "border border-slate-400 bg-transparent"
          }`}
        />
      ))}
    </output>
  );
}
